import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { ArrowLeft, Check, ExternalLink } from "lucide-react";
import axios from "axios";
import clsx from "clsx";
import * as api from "../../api/statoLavori";
import { getErrorMessage } from "../../api/client";
import { SERVIZIO_PRECREDIT_LABELS } from "../../api/devices";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { LinkIndietro } from "../../components/ui/LinkIndietro";
import {
  APRI_RECORD,
  ORIGINE_BOZZA,
  PASSI_CON_RECORD,
  ROTTA_RECORD,
  TONO_STATO,
  dataIt,
  etichettaStato,
  etichetteMancanti,
  gruppiDiPassi,
  istanteIt,
  linkConfermaBozza,
  nomeCassaforte,
  percentuale,
  prossimoStatoManuale,
  responsabileAMano,
} from "./presentazione";
import {
  FinestraCassaforte,
  FinestraChiudiPratica,
  FinestraSedeGiaAttiva,
  FinestraCollega,
  FinestraCorreggi,
  FinestraDatiOrdine,
  FinestraDispositivo,
  FinestraGiorno,
  FinestraMotivo,
  FinestraResponsabile,
  FinestraScartaBozza,
  FinestraServizio,
} from "./Finestre";
import { ServizioPratica, stileSegnalazione } from "./PezziPratica";

// ============================================================================
// STATO LAVORI — scheda della pratica (issue #211, PR B3)
//
// Come la tavola «Dettaglio» della bozza grafica: intestazione, avanzamento,
// dati dell'ordine e i dieci passi raggruppati. Lo stato di ogni passo arriva
// calcolato dal backend; ogni scrittura risponde con la pratica ricalcolata, e
// la scheda mostra quella. Le regole (stato successivo, voci mancanti, 409)
// stanno nel backend: qui si mostrano i suoi messaggi, nel punto dove servono.
// ============================================================================

/** Le spunte del riquadro «Dati dell'ordine»: colonne della pratica, ognuna col suo giorno. */
const SPUNTE_ORDINE: { campo: keyof api.DatiOrdine["spunte"]; etichetta: string }[] = [
  { campo: "cartella_punto_vendita_il", etichetta: "Cartella con le sottocartelle dei punti vendita" },
  // Stesso documento della voce del passo 8: qui «compilata», là «inviata» (decisione del 22/10).
  { campo: "scheda_btv_il", etichetta: "Scheda BTV (scheda di attivazione della sede) compilata" },
  { campo: "elenco_utenti_il", etichetta: "Elenco utenti compilato" },
];

/** Un link con l'aspetto del bottone secondario: porta a un'altra pagina, non esegue un'azione. */
const LINK_BOTTONE =
  "inline-flex items-center justify-center rounded-lg border border-line bg-white text-sm font-semibold text-primary shadow-card transition-colors hover:bg-primary/5";

type FinestraAperta =
  | { tipo: "avanza"; passo: api.Passo; stato: { codice: string; etichetta: string; ultimo: boolean } }
  | { tipo: "voce"; passo: api.Passo; voce: api.Voce }
  | { tipo: "non_necessario"; passo: api.Passo }
  | { tipo: "correggi"; passo: api.Passo }
  | { tipo: "collega"; passo: api.Passo }
  | { tipo: "responsabile"; passo: api.Passo }
  | { tipo: "chiudi" }
  | { tipo: "cassaforte"; azione: "chiudi" | "riapri" }
  | { tipo: "sede_gia_attiva" }
  | { tipo: "dispositivo" }
  | { tipo: "dati_ordine" }
  | { tipo: "spunta_ordine"; campo: keyof api.DatiOrdine["spunte"]; etichetta: string }
  | { tipo: "servizio" }
  | { tipo: "scarta_bozza"; passo: api.Passo; bozzaId: string };

function stato409(err: unknown): boolean {
  return axios.isAxiosError(err) && err.response?.status === 409;
}

export default function PraticaDettaglio() {
  const { id = "" } = useParams<{ id: string }>();
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const puoModificare = hasPermission("stato_lavori:update");

  const [dati, setDati] = useState<api.DettaglioPratica | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [nonTrovata, setNonTrovata] = useState(false);
  const [finestra, setFinestra] = useState<FinestraAperta | null>(null);
  /** L'errore dell'ultima azione diretta su un passo (togli una spunta), mostrato nel passo. */
  const [erroriPasso, setErroriPasso] = useState<Partial<Record<api.TipoPasso, string>>>({});
  /** L'errore di «Riapri pratica» (es. cassaforte chiusa), mostrato nel riquadro della chiusura. */
  const [erroreRiapri, setErroreRiapri] = useState<string | null>(null);

  const ricarica = useCallback(async () => {
    try {
      setDati(await api.getPratica(id));
      setErrore(null);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) setNonTrovata(true);
      else setErrore(getErrorMessage(err, "Impossibile caricare la pratica."));
    }
  }, [id]);

  useEffect(() => {
    setDati(null);
    setNonTrovata(false);
    void ricarica();
  }, [ricarica]);

  // Dal «Da fare» della dashboard (`#passo-<tipo>`, `#servizio`): appena la
  // pratica c'è, si va al punto indicato.
  const { hash } = useLocation();
  const caricata = dati !== null;
  useEffect(() => {
    if (caricata && hash) document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView({ block: "start" });
  }, [caricata, hash]);

  /**
   * Esegue una scrittura: con successo mostra la pratica ricalcolata, con un
   * errore restituisce il messaggio del backend. Su un 409 (qualcun altro ha
   * cambiato la pratica, o la regola lo vieta) ricarica anche la scheda, così
   * quello che si vede è quello che c'è.
   */
  const esegui = useCallback(
    async (azione: () => Promise<api.DettaglioPratica>, successo: string): Promise<string | null> => {
      try {
        setDati(await azione());
        setErroriPasso({});
        setErroreRiapri(null);
        showToast(successo, "success");
        return null;
      } catch (err) {
        if (stato409(err)) void ricarica();
        return getErrorMessage(err, "Operazione non riuscita.");
      }
    },
    [ricarica, showToast]
  );

  if (nonTrovata) {
    return (
      <div className="py-12 text-center">
        <p className="text-sm text-ink/60">Pratica non trovata.</p>
        <LinkIndietro to="/stato-lavori" className="mt-2 inline-block text-sm font-semibold text-primary hover:underline">
          Torna a Stato lavori
        </LinkIndietro>
      </div>
    );
  }
  if (errore && !dati) {
    return (
      <div className="flex flex-col items-center gap-3 py-12 text-center">
        <p className="text-sm text-[#b91c1c]">{errore}</p>
        <Button variant="secondary" size="sm" onClick={() => void ricarica()}>
          Riprova
        </Button>
      </div>
    );
  }
  if (!dati) return <p className="py-12 text-center text-sm text-ink/50">Caricamento…</p>;

  const modificabile = puoModificare && !dati.chiusa;
  const pct = percentuale(dati.avanzamento);
  const nomeUtente = (uid: string | null | undefined) => (uid ? (dati.utenti[uid] ?? null) : null);
  const altreSegnalazioni = dati.segnalazioni.filter((s) => s.codice !== "promemoria_ordini" && s.codice !== "chiusa");
  // Il backend lo manda sempre; se mancasse, il pulsante sparisce invece di rompere la scheda.
  const passiSedeGiaAttiva = dati.passi_sede_gia_attiva ?? [];
  // Anche questa: senza, il riquadro della chiusura ripiega sul testo della segnalazione.
  const chiusuraCassaforte = dati.cassaforte.chiusura ?? null;
  /** « (il …, da …)» di una chiusura. */
  const quandoEChi = (c: { at: string; da: string | null }) =>
    ` (il ${istanteIt(c.at)}${nomeUtente(c.da) ? `, da ${nomeUtente(c.da)}` : ""})`;

  async function togliVoce(passo: api.Passo, voce: api.Voce) {
    const esito = await esegui(() => api.togliVoce(id, passo.tipo, voce.codice), `Spunta tolta: ${voce.etichetta}.`);
    if (esito) setErroriPasso((e) => ({ ...e, [passo.tipo]: esito }));
  }

  async function annullaNonNecessario(passo: api.Passo) {
    const esito = await esegui(() => api.annullaNonNecessario(id, passo.tipo), `${passo.titolo}: «non necessario» annullato.`);
    if (esito) setErroriPasso((e) => ({ ...e, [passo.tipo]: esito }));
  }

  /** «Annulla scarto» (PR C2): il 409 «non più dovuta» resta nel passo, col motivo del backend. */
  async function annullaScarto(passo: api.Passo) {
    if (!passo.bozza_scartata) return;
    const bozzaId = passo.bozza_scartata.id;
    const esito = await esegui(async () => {
      await api.annullaScartoBozza(bozzaId);
      return api.getPratica(id);
    }, `${passo.titolo}: scarto annullato, la bozza è di nuovo da confermare.`);
    if (esito) setErroriPasso((e) => ({ ...e, [passo.tipo]: esito }));
  }

  async function togliSpuntaOrdine(campo: keyof api.DatiOrdine["spunte"]) {
    const esito = await esegui(() => api.modificaPratica(id, { [campo]: null }), "Spunta tolta.");
    if (esito) showToast(esito, "error");
  }

  async function riapri() {
    setErroreRiapri(await esegui(() => api.riapriPratica(id), "Pratica riaperta."));
  }

  async function servizioDalDispositivo() {
    const esito = await esegui(
      () => api.modificaPratica(id, { tipo_servizio_precredit: dati!.servizio_dispositivo }),
      "Servizio della pratica allineato al dispositivo."
    );
    if (esito) showToast(esito, "error");
  }

  return (
    <div className="flex max-w-[1100px] flex-col gap-6">
      <LinkIndietro to="/stato-lavori" className="inline-flex items-center gap-1.5 text-sm text-ink/50 hover:text-ink">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Stato lavori
      </LinkIndietro>

      {/* --- Intestazione ----------------------------------------------------- */}
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink">
            {dati.cliente.nome} – {dati.sede.nome ?? "Sede senza nome"}
          </h1>
          <p className="mt-0.5 text-sm text-ink/60">
            {[dati.sede.citta, `pratica avviata il ${istanteIt(dati.avviata_il)}`].filter(Boolean).join(" · ")}
          </p>
          <div className="mt-2.5 flex flex-wrap gap-2">
            <ServizioPratica servizio={dati.servizio} testoDaVerificare="Servizio da verificare" />
            <Badge>Cassaforte: {nomeCassaforte(dati.cassaforte)}</Badge>
            {modificabile && !dati.cassaforte.device_id && (
              <button
                type="button"
                onClick={() => setFinestra({ tipo: "dispositivo" })}
                className="rounded-full text-xs font-semibold text-primary hover:underline"
              >
                Associa dispositivo…
              </button>
            )}
            {dati.prima_wink_on_cash && dati.servizio.valore === "wink_on_cash" && <Badge>Prima Wink on Cash del cliente</Badge>}
            {dati.condivisa_con.map((c) => (
              <Link key={c.pratica_id} to={`/stato-lavori/${c.pratica_id}`} className="rounded-full hover:underline">
                <Badge>
                  Condivisa con {c.cliente.nome}
                  {c.sede.nome ? ` – ${c.sede.nome}` : ""}
                </Badge>
              </Link>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to={`/sedi/${dati.sede.id}`} className={clsx(LINK_BOTTONE, "h-10 px-4")}>
            Scheda sede in Anagrafiche
          </Link>
          <Link to={`/clients/${dati.cliente.id}`} className={clsx(LINK_BOTTONE, "h-10 px-4")}>
            Scheda cliente
          </Link>
          {modificabile && passiSedeGiaAttiva.length > 0 && (
            <Button variant="secondary" onClick={() => setFinestra({ tipo: "sede_gia_attiva" })}>
              Sede già attiva…
            </Button>
          )}
          {modificabile && (
            <Button variant="ghost" onClick={() => setFinestra({ tipo: "chiudi" })}>
              Chiudi pratica…
            </Button>
          )}
          {modificabile && (
            <Button variant="ghost" onClick={() => setFinestra({ tipo: "cassaforte", azione: "chiudi" })}>
              Chiudi cassaforte…
            </Button>
          )}
        </div>
      </header>

      {dati.chiusa && (
        <div role="status" className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-line bg-black/5 px-4 py-3 text-sm text-ink/80">
          <div className="flex flex-col gap-1">
            {dati.chiusura && (
              <p>
                Pratica chiusa{dati.chiusura.motivo ? `: ${dati.chiusura.motivo}` : ""}
                {quandoEChi(dati.chiusura)}.
              </p>
            )}
            {chiusuraCassaforte && (
              <p>
                Cassaforte chiusa{chiusuraCassaforte.motivo ? `: ${chiusuraCassaforte.motivo}` : ""}
                {quandoEChi(chiusuraCassaforte)}, per tutte le sue pratiche.
              </p>
            )}
            {!dati.chiusura && !chiusuraCassaforte && <p>{dati.segnalazioni.find((s) => s.codice === "chiusa")?.testo ?? "Pratica chiusa"}.</p>}
            <p>Una pratica chiusa non si modifica.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {/* Si riapre solo la pratica chiusa lei: con la cassaforte chiusa il backend risponde 409, e il messaggio resta qui. */}
            {puoModificare && dati.chiusura && (
              <Button size="sm" variant="secondary" onClick={() => void riapri()}>
                Riapri pratica
              </Button>
            )}
            {puoModificare && chiusuraCassaforte && (
              <Button size="sm" variant="secondary" onClick={() => setFinestra({ tipo: "cassaforte", azione: "riapri" })}>
                Riapri cassaforte…
              </Button>
            )}
          </div>
          {erroreRiapri && (
            <p role="alert" className="w-full rounded bg-[#ef4444]/10 px-2 py-1.5 text-xs text-[#b91c1c]">
              {erroreRiapri}
            </p>
          )}
        </div>
      )}

      {/* --- Servizio da verificare (variante 2) ------------------------------- */}
      {dati.servizio.da_verificare && (
        <section id="servizio" className="scroll-mt-16 rounded-card border border-[#F0D9A8] bg-[#FFF6E3] p-4">
          <p className="text-sm text-[#4A2E00]">
            {dati.servizio_dichiarato && dati.servizio_dispositivo
              ? `Il servizio indicato all'avvio della pratica (${SERVIZIO_PRECREDIT_LABELS[dati.servizio_dichiarato]}) non coincide con quello del dispositivo (${SERVIZIO_PRECREDIT_LABELS[dati.servizio_dispositivo]}).`
              : "Il servizio della pratica non è stato indicato."}{" "}
            I passi che riguardano solo Wink on Cash restano come sono finché non verifichi.
          </p>
          {modificabile && (
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" onClick={() => setFinestra({ tipo: "servizio" })}>
                {dati.servizio_dichiarato ? "Correggi il servizio della pratica" : "Indica il servizio"}
              </Button>
              {dati.servizio_dichiarato && dati.servizio_dispositivo && (
                <Button size="sm" variant="secondary" onClick={() => void servizioDalDispositivo()}>
                  Il dispositivo è corretto
                </Button>
              )}
            </div>
          )}
        </section>
      )}

      {/* --- Avanzamento ------------------------------------------------------- */}
      <section className="rounded-card border border-line bg-white p-5 shadow-card">
        <div className="mb-2.5 flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-sm font-bold uppercase tracking-wide text-ink/50">Avanzamento</h2>
          <span className="text-sm font-semibold">
            {dati.avanzamento.completati} su {dati.avanzamento.necessari} passi completati
            {dati.avanzamento.non_necessari > 0 && (
              <span className="font-normal text-ink/60">
                {" "}
                · {dati.avanzamento.non_necessari} {dati.avanzamento.non_necessari === 1 ? "non necessario" : "non necessari"}
              </span>
            )}
          </span>
        </div>
        <div
          className="h-2.5 overflow-hidden rounded-full bg-[#EDEFEB]"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={dati.avanzamento.necessari}
          aria-valuenow={dati.avanzamento.completati}
          aria-label="Passi completati"
        >
          <div className="h-2.5 bg-primary" style={{ width: `${pct}%` }} />
        </div>
        {(dati.promemoria.length > 0 || altreSegnalazioni.length > 0) && (
          <div className="mt-3.5 flex flex-wrap gap-2">
            {altreSegnalazioni.map((s, i) => (
              <span
                key={`s${i}`}
                className={clsx("rounded px-2 py-1.5 text-xs", stileSegnalazione(s))}
              >
                {s.testo}
              </span>
            ))}
            {dati.promemoria.map((p) => (
              <Link
                key={`${p.tipo}-${p.attivita_id}`}
                to={`${ROTTA_RECORD[p.modulo]}?open=${p.attivita_id}`}
                className="rounded bg-amber-50 px-2 py-1.5 text-xs text-amber-900 hover:underline"
              >
                {p.testo}
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* --- Dati dell'ordine -------------------------------------------------- */}
      <section className="rounded-card border border-line bg-white p-5 shadow-card">
        <div className="mb-3.5 flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-sm font-bold uppercase tracking-wide text-ink/50">Dati dell&apos;ordine</h2>
          {modificabile ? (
            <Button size="sm" variant="secondary" onClick={() => setFinestra({ tipo: "dati_ordine" })}>
              Modifica
            </Button>
          ) : (
            <span className="text-xs text-ink/55">Da avere appena arriva l&apos;ordine</span>
          )}
        </div>
        <DatiOrdine d={dati.dati_ordine} />
        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 border-t border-line pt-3.5">
          {SPUNTE_ORDINE.map((s) => {
            const il = dati.dati_ordine.spunte[s.campo];
            return (
              <label key={s.campo} className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={il !== null}
                  disabled={!modificabile}
                  onChange={() =>
                    il !== null ? void togliSpuntaOrdine(s.campo) : setFinestra({ tipo: "spunta_ordine", campo: s.campo, etichetta: s.etichetta })
                  }
                />
                {s.etichetta}
                {il && <span className="text-xs text-ink/55">il {dataIt(il)}</span>}
              </label>
            );
          })}
        </div>
      </section>

      {/* --- Passi ------------------------------------------------------------- */}
      <section className="rounded-card border border-line bg-white p-5 shadow-card">
        <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-sm font-bold uppercase tracking-wide text-ink/50">Passi nel dettaglio</h2>
          <span className="text-xs text-ink/55">Ogni passo ha i suoi stati intermedi: l&apos;ultimo lo chiude</span>
        </div>
        {gruppiDiPassi(dati.passi).map((g) => (
          <div key={g.titolo} className="mt-4">
            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-ink/55">{g.titolo}</h3>
            {g.passi.map((p) => (
              <RigaPasso
                key={p.tipo}
                pratica={dati}
                passo={p}
                modificabile={modificabile}
                errore={erroriPasso[p.tipo] ?? null}
                nomeUtente={nomeUtente}
                apri={setFinestra}
                togliVoce={togliVoce}
                annullaNonNecessario={annullaNonNecessario}
                annullaScarto={annullaScarto}
              />
            ))}
          </div>
        ))}
      </section>

      {/* --- Finestre ---------------------------------------------------------- */}
      {finestra?.tipo === "avanza" && (
        <FinestraGiorno
          titolo={`${finestra.passo.titolo}: avanza a «${finestra.stato.etichetta}»`}
          etichettaConferma={`Avanza a: ${finestra.stato.etichetta}`}
          valore={
            finestra.stato.ultimo && finestra.passo.tipo === "conto_tecnico"
              ? { etichetta: "Numero del conto tecnico", iniziale: dati.valori.conto_tecnico }
              : finestra.stato.ultimo && finestra.passo.tipo === "codice_gdo"
                ? { etichetta: "Codice GDO", iniziale: dati.valori.codice_gdo }
                : undefined
          }
          onClose={() => setFinestra(null)}
          onConferma={({ il, valore }) => {
            const salvato = finestra.passo.tipo === "conto_tecnico" ? dati.valori.conto_tecnico : dati.valori.codice_gdo;
            // Il valore parte solo se è nuovo: con uno già salvato il backend usa quello.
            const conValore = finestra.stato.ultimo && valore && valore !== salvato ? { valore } : {};
            return esegui(
              () => api.avanzaPasso(id, finestra.passo.tipo, { stato: finestra.stato.codice, il, ...conValore }),
              `${finestra.passo.titolo}: ${finestra.stato.etichetta}.`
            );
          }}
        />
      )}
      {finestra?.tipo === "voce" && (
        <FinestraGiorno
          titolo={finestra.voce.etichetta}
          spiegazione={`Passo: ${finestra.passo.titolo}`}
          etichettaConferma="Spunta"
          conNota
          onClose={() => setFinestra(null)}
          onConferma={({ il, nota }) =>
            esegui(
              () => api.spuntaVoce(id, finestra.passo.tipo, { codice: finestra.voce.codice, fatta_il: il, nota: nota || null }),
              `Spuntata: ${finestra.voce.etichetta}.`
            )
          }
        />
      )}
      {finestra?.tipo === "non_necessario" && (
        <FinestraMotivo
          passo={finestra.passo}
          onClose={() => setFinestra(null)}
          onConferma={(motivo) =>
            esegui(() => api.segnaNonNecessario(id, finestra.passo.tipo, motivo), `${finestra.passo.titolo}: non necessario.`)
          }
        />
      )}
      {finestra?.tipo === "correggi" && (
        <FinestraCorreggi
          passo={finestra.passo}
          onClose={() => setFinestra(null)}
          onConferma={(verso) => esegui(() => api.correggiPasso(id, finestra.passo.tipo, verso), `${finestra.passo.titolo}: corretto.`)}
        />
      )}
      {finestra?.tipo === "collega" && (
        <FinestraCollega
          pratica={dati}
          passo={finestra.passo}
          onClose={() => setFinestra(null)}
          onConferma={(recordId) =>
            esegui(
              () => api.collegaRecord(id, finestra.passo.tipo, recordId),
              recordId ? `${finestra.passo.titolo}: record collegato.` : `${finestra.passo.titolo}: collegamento tolto.`
            )
          }
        />
      )}
      {finestra?.tipo === "responsabile" && (
        <FinestraResponsabile
          passo={finestra.passo}
          onClose={() => setFinestra(null)}
          onConferma={(utenteId) =>
            esegui(
              () => api.impostaResponsabile(id, finestra.passo.tipo, utenteId),
              utenteId ? `${finestra.passo.titolo}: responsabile salvato.` : `${finestra.passo.titolo}: responsabile tolto.`
            )
          }
        />
      )}
      {finestra?.tipo === "sede_gia_attiva" && (
        <FinestraSedeGiaAttiva
          passi={dati.passi.filter((p) => passiSedeGiaAttiva.includes(p.tipo))}
          condivisaCon={dati.condivisa_con}
          onClose={() => setFinestra(null)}
          onConferma={() => esegui(() => api.segnaSedeGiaAttiva(id), "Passi preliminari segnati «non necessario».")}
        />
      )}
      {finestra?.tipo === "chiudi" && (
        <FinestraChiudiPratica onClose={() => setFinestra(null)} onConferma={(motivo) => esegui(() => api.chiudiPratica(id, motivo), "Pratica chiusa.")} />
      )}
      {finestra?.tipo === "cassaforte" && (
        <FinestraCassaforte
          azione={finestra.azione}
          condivisaCon={dati.condivisa_con}
          onClose={() => setFinestra(null)}
          onConferma={(motivo) =>
            // La cassaforte risponde con le righe delle sue pratiche: la scheda si rilegge.
            esegui(
              async () => {
                await (finestra.azione === "chiudi" ? api.chiudiCassaforte : api.riapriCassaforte)(dati.cassaforte.id, motivo);
                return api.getPratica(id);
              },
              finestra.azione === "chiudi" ? "Cassaforte chiusa." : "Cassaforte riaperta."
            )
          }
        />
      )}
      {finestra?.tipo === "dispositivo" && (
        <FinestraDispositivo
          pratica={dati}
          onClose={() => setFinestra(null)}
          onConferma={(deviceId) =>
            // La cassaforte risponde con le righe delle sue pratiche: la scheda si rilegge.
            esegui(async () => {
              await api.associaDispositivo(dati.cassaforte.id, deviceId);
              return api.getPratica(id);
            }, "Dispositivo associato alla cassaforte.")
          }
        />
      )}
      {finestra?.tipo === "dati_ordine" && (
        <FinestraDatiOrdine
          pratica={dati}
          onClose={() => setFinestra(null)}
          onConferma={(campi) => esegui(() => api.modificaPratica(id, campi), "Dati dell'ordine salvati.")}
        />
      )}
      {finestra?.tipo === "spunta_ordine" && (
        <FinestraGiorno
          titolo={finestra.etichetta}
          etichettaConferma="Spunta"
          onClose={() => setFinestra(null)}
          onConferma={({ il }) => esegui(() => api.modificaPratica(id, { [finestra.campo]: il }), `Spuntata: ${finestra.etichetta}.`)}
        />
      )}
      {finestra?.tipo === "scarta_bozza" && (
        <FinestraScartaBozza
          passo={finestra.passo}
          onClose={() => setFinestra(null)}
          onConferma={(motivo) =>
            esegui(async () => {
              await api.scartaBozza(finestra.bozzaId, motivo);
              return api.getPratica(id);
            }, `${finestra.passo.titolo}: bozza scartata.`)
          }
        />
      )}
      {finestra?.tipo === "servizio" && (
        <FinestraServizio
          iniziale={dati.servizio_dichiarato}
          onClose={() => setFinestra(null)}
          onConferma={(servizio) =>
            esegui(() => api.modificaPratica(id, { tipo_servizio_precredit: servizio }), "Servizio della pratica salvato.")
          }
        />
      )}
    </div>
  );
}

function DatiOrdine({ d }: { d: api.DatiOrdine }) {
  const indirizzo = [d.indirizzo.indirizzo, [d.indirizzo.cap, d.indirizzo.citta].filter(Boolean).join(" "), d.indirizzo.provincia ? `(${d.indirizzo.provincia})` : null]
    .filter(Boolean)
    .join(", ");
  const interlocutore = [d.interlocutore.nome, d.interlocutore.email, d.interlocutore.telefono].filter((x): x is string => Boolean(x));
  const suggerito = d.suggerimenti_interlocutore.referente_cliente;
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 xl:grid-cols-4">
      <div>
        <dt className="text-xs text-ink/60">Installare entro</dt>
        <dd className="mt-0.5 text-sm font-semibold">{dataIt(d.installare_entro)}</dd>
      </div>
      <div>
        <dt className="text-xs text-ink/60">Indirizzo di installazione</dt>
        <dd className="mt-0.5 text-sm font-semibold">{indirizzo || "Indirizzo della sede non indicato"}</dd>
        <dd className="text-xs text-ink/55">Dalla scheda sede</dd>
      </div>
      <div>
        <dt className="text-xs text-ink/60">Interlocutore operativo</dt>
        {interlocutore.length > 0 ? (
          interlocutore.map((riga, i) => (
            <dd key={i} className={clsx("break-words text-sm font-semibold", i === 0 && "mt-0.5")}>
              {riga}
            </dd>
          ))
        ) : (
          <dd className="mt-0.5 text-sm font-semibold">Non indicato</dd>
        )}
        {interlocutore.length === 0 && suggerito && (
          <dd className="text-xs text-ink/55">
            Suggerito dal referente del cliente: {[suggerito.nome, suggerito.email].filter(Boolean).join(" · ")}. Da confermare.
          </dd>
        )}
      </div>
      <div>
        <dt className="text-xs text-ink/60">Cartella della pratica</dt>
        <dd className="mt-0.5 text-sm font-semibold">
          {d.cartella_url ? (
            <a href={d.cartella_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
              Apri la cartella
              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            </a>
          ) : (
            "Non indicata"
          )}
        </dd>
      </div>
    </dl>
  );
}

/** Un passo: stato, stati intermedi, spunte e azioni. */
function RigaPasso({
  pratica,
  passo: p,
  modificabile,
  errore,
  nomeUtente,
  apri,
  togliVoce,
  annullaNonNecessario,
  annullaScarto,
}: {
  pratica: api.DettaglioPratica;
  passo: api.Passo;
  modificabile: boolean;
  errore: string | null;
  nomeUtente: (id: string | null | undefined) => string | null;
  apri: (f: FinestraAperta) => void;
  togliVoce: (passo: api.Passo, voce: api.Voce) => Promise<void>;
  annullaNonNecessario: (passo: api.Passo) => Promise<void>;
  annullaScarto: (passo: api.Passo) => Promise<void>;
}) {
  const fatto = p.stato === "completato";
  const nonNecessarioAMano = p.non_necessario !== null && !p.non_necessario.automatico;
  // Su un passo segnato «non necessario» a mano il backend rifiuta avanza,
  // correggi e spunte: le azioni non si offrono, resta «Annulla».
  const azioniPasso = modificabile && !nonNecessarioAMano;
  // Un passo concluso (anche dal dato, come il codice GDO della relazione) non si avanza.
  const prossimo = fatto || p.stato === "non_necessario" ? null : prossimoStatoManuale(p);
  const mancanti = prossimo?.ultimo && p.tipo === "documenti_vigilanza" ? etichetteMancanti(p) : [];
  const raggiuntiAMano = p.stati.filter((s) => s.raggiunto && s.fonte !== "ricavato");
  const corrente = [...p.stati].reverse().find((s) => s.raggiunto) ?? null;
  // Passo 8: si vedono le voci richieste, e quelle spuntate che non servono più (non contano).
  const voci = p.tipo === "documenti_vigilanza" ? p.voci.filter((v) => v.richiesta || v.fatta) : p.voci;

  return (
    <div id={`passo-${p.tipo}`} className="flex scroll-mt-16 flex-wrap items-start gap-x-3.5 gap-y-2.5 border-t border-line py-3.5">
      <div
        className={clsx(
          "flex h-[26px] w-[26px] flex-none items-center justify-center rounded-full text-xs font-bold",
          fatto ? "bg-[#15803d] text-white" : "bg-primary/[0.08] text-ink"
        )}
      >
        {fatto ? <Check className="h-3.5 w-3.5" aria-label="Completato" /> : <span>{p.numero}</span>}
      </div>
      <div className="min-w-0 flex-[1_1_380px]">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h4 className="text-sm font-semibold text-ink">{p.titolo}</h4>
          <Badge color={TONO_STATO[p.stato]}>{etichettaStato(p, pratica.etichette_stato)}</Badge>
        </div>
        <p className="mt-0.5 text-xs leading-relaxed text-ink/65">
          {p.nota}
          {p.richiesta_a && (
            <>
              {" "}
              La richiesta si invia a <span className="select-all font-semibold text-ink">{p.richiesta_a}</span>.
            </>
          )}
        </p>

        {p.in_attesa_di && p.stato === "da_fare" && <p className="mt-1 text-xs text-ink/65">In attesa di: {p.in_attesa_di}</p>}
        {p.motivo && <p className="mt-1 text-xs text-[#b91c1c]">{p.motivo}</p>}
        {p.non_necessario && (
          <p className="mt-1 text-xs text-ink/65">
            {p.non_necessario.automatico ? "Non necessario: " : "Segnato non necessario: "}
            {p.non_necessario.motivo}
            {p.non_necessario.pratica_id && (
              <>
                {" "}
                (
                <Link to={`/stato-lavori/${p.non_necessario.pratica_id}`} className="font-semibold text-primary hover:underline">
                  apri la pratica
                </Link>
                )
              </>
            )}
            {!p.non_necessario.automatico &&
              ` — ${[nomeUtente(p.non_necessario.da), p.non_necessario.at ? `il ${istanteIt(p.non_necessario.at)}` : null].filter(Boolean).join(", ")}`}
          </p>
        )}
        {p.da_verificare.map((m) => (
          <p key={m} className="mt-1 rounded bg-amber-50 px-2 py-1 text-xs text-amber-900">
            Da verificare: {m}
          </p>
        ))}

        {/* Stati intermedi, con la freccia fra uno e l'altro come nella bozza. */}
        {p.stati.length > 0 && (
          <ol className="mt-2.5 flex flex-wrap items-center gap-1.5" aria-label="Stati intermedi">
            {p.stati.map((s, i) => {
              const eCorrente = corrente?.codice === s.codice && !fatto;
              const chi = nomeUtente(s.registrato_da);
              return (
                <li key={s.codice} className="inline-flex items-center gap-1.5">
                  <span
                    title={s.raggiunto ? [s.il ? `il ${dataIt(s.il)}` : null, chi ? `da ${chi}` : null, s.fonte === "ricavato" ? "dal record" : null].filter(Boolean).join(" ") : undefined}
                    className={clsx(
                      "inline-flex items-center whitespace-nowrap rounded-md px-2.5 py-1 text-xs font-semibold",
                      eCorrente ? "bg-primary text-white" : s.raggiunto ? "bg-[#22c55e]/10 text-[#15803d]" : "bg-black/5 text-ink/75"
                    )}
                  >
                    {s.raggiunto && !eCorrente ? "✓ " : ""}
                    {s.etichetta}
                  </span>
                  {i < p.stati.length - 1 && (
                    <span className="text-sm text-ink/45" aria-hidden="true">
                      ›
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
        )}

        {/* Il valore che chiude i passi 3 e 4, e i contratti accanto al passo 5 (sola informazione). */}
        {p.tipo === "conto_tecnico" && pratica.valori.conto_tecnico && (
          <p className="mt-2 text-sm">
            Conto tecnico: <span className="font-semibold">{pratica.valori.conto_tecnico}</span>
          </p>
        )}
        {p.tipo === "codice_gdo" && pratica.valori.codice_gdo && (
          <p className="mt-2 text-sm">
            Codice GDO: <span className="font-semibold">{pratica.valori.codice_gdo}</span>
          </p>
        )}
        {p.tipo === "contratto" && pratica.contratti_che_citano_la_sede.length > 0 && (
          <ul className="mt-2 flex flex-col gap-0.5 text-xs text-ink/65">
            {pratica.contratti_che_citano_la_sede.map((c) => (
              <li key={c.id}>
                <Link to={`/contratti/${c.id}`} className="font-semibold text-primary hover:underline">
                  {c.riferimento ?? "Contratto"}
                </Link>{" "}
                — {c.stato}
                {c.data_contratto ? `, del ${dataIt(c.data_contratto)}` : ""}
              </li>
            ))}
          </ul>
        )}

        {/* La bozza del record (passi 7 e 9, PR C2): diventa record solo dal form del modulo. */}
        {p.bozza && (
          <div className="mt-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
            <p className="text-sm font-semibold text-amber-900">Bozza da confermare</p>
            <p className="mt-0.5 text-xs text-amber-900/80">
              Dal {istanteIt(p.bozza.dal)}. {ORIGINE_BOZZA[p.bozza.tipo]} Diventa {p.bozza.tipo === "installazione" ? "un'installazione" : "un'attivazione"}{" "}
              solo confermandola: si apre il form, già compilato, e si salva.
            </p>
          </div>
        )}
        {p.bozza_scartata && (
          <p className="mt-2 text-xs text-ink/65">
            Bozza scartata
            {[p.bozza_scartata.at ? ` il ${istanteIt(p.bozza_scartata.at)}` : null, nomeUtente(p.bozza_scartata.da) ? ` da ${nomeUtente(p.bozza_scartata.da)}` : null]
              .filter(Boolean)
              .join(",")}
            {p.bozza_scartata.motivo ? `: ${p.bozza_scartata.motivo}` : ""}.
          </p>
        )}

        {voci.length > 0 && (
          <ul className="mt-2.5 flex flex-col gap-1.5">
            {voci.map((v) => (
              <RigaVoce
                key={v.codice}
                passo={p}
                voce={v}
                modificabile={azioniPasso}
                nomeUtente={nomeUtente}
                onSpunta={() => apri({ tipo: "voce", passo: p, voce: v })}
                onTogli={() => void togliVoce(p, v)}
              />
            ))}
          </ul>
        )}

        {errore && (
          <p role="alert" className="mt-2 rounded bg-[#ef4444]/10 px-2 py-1.5 text-xs text-[#b91c1c]">
            {errore}
          </p>
        )}

        <div className="mt-2.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
          <span className="text-xs text-ink/60">
            {[
              p.responsabile ? `Responsabile: ${p.responsabile.nome}` : null,
              corrente && corrente.fonte !== "ricavato" && corrente.il
                ? `${corrente.etichetta} il ${dataIt(corrente.il)}${nomeUtente(corrente.registrato_da) ? ` da ${nomeUtente(corrente.registrato_da)}` : ""}`
                : null,
              // Con un record l'azione è il pulsante «Apri…»: non si ripete qui.
              !fatto && p.prossima_azione && !prossimo && !p.record ? p.prossima_azione : null,
            ]
              .filter(Boolean)
              .join(" · ") || (fatto || p.stato === "non_necessario" ? null : "Nessun aggiornamento")}
          </span>
          <span className="flex flex-wrap items-center gap-1.5">
            {p.record && (
              <Link to={`${ROTTA_RECORD[p.record.modulo]}?open=${p.record.id}`} className={clsx(LINK_BOTTONE, "h-8 px-3")}>
                {APRI_RECORD[p.record.modulo]}
              </Link>
            )}
            {modificabile && p.bozza && (
              <>
                <Link to={linkConfermaBozza(p.bozza, pratica.id)} className={clsx(LINK_BOTTONE, "h-8 px-3")}>
                  Conferma bozza…
                </Link>
                <Button size="sm" variant="ghost" onClick={() => apri({ tipo: "scarta_bozza", passo: p, bozzaId: p.bozza!.id })}>
                  Scarta…
                </Button>
              </>
            )}
            {modificabile && p.bozza_scartata && (
              <Button size="sm" variant="ghost" onClick={() => void annullaScarto(p)}>
                Annulla scarto
              </Button>
            )}
            {azioniPasso && prossimo && (
              <Button
                size="sm"
                variant="secondary"
                disabled={mancanti.length > 0}
                aria-describedby={mancanti.length > 0 ? `mancano-${p.tipo}` : undefined}
                onClick={() => apri({ tipo: "avanza", passo: p, stato: prossimo })}
              >
                Avanza a: {prossimo.etichetta}
              </Button>
            )}
            {azioniPasso && raggiuntiAMano.length > 0 && (
              <Button size="sm" variant="ghost" onClick={() => apri({ tipo: "correggi", passo: p })}>
                Correggi…
              </Button>
            )}
            {modificabile && PASSI_CON_RECORD.includes(p.tipo) && (
              <Button size="sm" variant="ghost" onClick={() => apri({ tipo: "collega", passo: p })}>
                Collega…
              </Button>
            )}
            {modificabile && responsabileAMano(p) && (
              <Button size="sm" variant="ghost" onClick={() => apri({ tipo: "responsabile", passo: p })}>
                Responsabile…
              </Button>
            )}
            {modificabile && nonNecessarioAMano && (
              <Button size="sm" variant="ghost" onClick={() => void annullaNonNecessario(p)}>
                Annulla «non necessario»
              </Button>
            )}
            {azioniPasso && p.stato !== "non_necessario" && !fatto && (
              <Button size="sm" variant="ghost" onClick={() => apri({ tipo: "non_necessario", passo: p })}>
                Non necessario…
              </Button>
            )}
          </span>
        </div>
        {mancanti.length > 0 && (
          <p id={`mancano-${p.tipo}`} className="mt-1.5 text-right text-xs text-ink/65">
            Mancano: {mancanti.join("; ")}
          </p>
        )}
      </div>
    </div>
  );
}

function RigaVoce({
  passo,
  voce: v,
  modificabile,
  nomeUtente,
  onSpunta,
  onTogli,
}: {
  passo: api.Passo;
  voce: api.Voce;
  modificabile: boolean;
  nomeUtente: (id: string | null | undefined) => string | null;
  onSpunta: () => void;
  onTogli: () => void;
}) {
  const vig = passo.vigilanza;
  const ePreavviso = passo.tipo === "documenti_vigilanza" && v.codice === "richiesta_preavviso";
  const etichetta = v.fonte === "ricavata" && v.nota ? `${v.etichetta}: ${v.nota}` : v.etichetta;
  const dettagli = [
    v.fatta && v.fatta_il ? `il ${dataIt(v.fatta_il)}` : null,
    v.fonte === "manuale" && v.nota ? v.nota : null,
    v.fatta && nomeUtente(v.registrata_da) ? `da ${nomeUtente(v.registrata_da)}` : null,
    !v.richiesta ? "non richiesta per questa pratica: non conta" : null,
    !v.disponibile && !v.fatta ? "serve il dispositivo sulla cassaforte" : null,
    v.fonte === "ricavata" && passo.tipo === "attivazione" ? "dall'Allestimento sistemi" : null,
    ePreavviso && vig ? (vig.scadenza_preavviso ? `da inviare entro il ${dataIt(vig.scadenza_preavviso)}` : vig.preavviso_non_calcolabile) : null,
  ].filter(Boolean);
  const interattiva = v.fonte === "manuale" && modificabile && (v.fatta || v.disponibile);

  return (
    <li className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5">
      <label className={clsx("inline-flex items-center gap-2 text-sm", !v.richiesta && "text-ink/55")}>
        <input
          type="checkbox"
          checked={v.fatta}
          disabled={!interattiva}
          onChange={() => (v.fatta ? onTogli() : onSpunta())}
          aria-describedby={dettagli.length > 0 ? `voce-${passo.tipo}-${v.codice}` : undefined}
        />
        {etichetta}
      </label>
      {dettagli.length > 0 && (
        <span id={`voce-${passo.tipo}-${v.codice}`} className="text-xs text-ink/60">
          {dettagli.join(" · ")}
        </span>
      )}
      {ePreavviso && vig?.preavviso_non_rispettato && (
        <span className="rounded bg-amber-50 px-1.5 py-0.5 text-xs text-amber-900">inviata con meno di 5 giorni lavorativi di preavviso</span>
      )}
    </li>
  );
}
